import React, { useRef, useState } from "react";
import { BsArrowReturnRight, BsBoxArrowUpRight } from "react-icons/bs";
import { accentStyle } from "../../../../helpers/utils/stylecss-utils";
import "./index.css";

/** A button's own icon (an image URL), or nothing. */
export function ButtonIcon({ icon, alt }) {
  if (!icon) return null;
  return <img src={icon} alt={alt || ""} className="chat-reply-icon" />;
}

/**
 * Suggested replies, stacked under the bot's message ("↳ Configuring your
 * account"). Tinted with the widget's accent; the one the visitor picked
 * stays filled so the conversation shows what was chosen.
 *
 * buttons: [{ title, payload, actionType, icon, url, ... }]
 * onSelect(button): called with the clicked button.
 */
export default function QuickReplies({ buttons, onSelect, disabled, chatBotSetting, className = "" }) {
  const [picked, setPicked] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const inFlight = useRef(false);
  const items = (Array.isArray(buttons) ? buttons : []).filter(button => button && button.title);
  if (items.length === 0) return null;

  const select = async (button, index) => {
    if (disabled || inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setError(false);
    setPicked(index);
    try {
      await onSelect?.(button);
    } catch {
      setPicked(null);
      setError(true);
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };

  return (
    <div className={`chat-replies ${className}`.trim()} style={accentStyle(chatBotSetting)} role="group" aria-label="Suggested replies">
      {items.map((button, index) => {
        const external = button.actionType === "web_url";
        return (
          <button
            key={button.id || index}
            type="button"
            className="chat-reply"
            style={{ animationDelay: `${Math.min(index, 6) * 50}ms` }}
            aria-pressed={picked === index}
            disabled={disabled || pending}
            aria-busy={pending && picked === index}
            onClick={() => select(button, index)}
          >
            {pending && picked === index ? <span className="chat-reply-spinner" aria-hidden="true" /> : button.icon ? <ButtonIcon icon={button.icon} /> : <BsArrowReturnRight className="chat-reply-arrow" aria-hidden="true" />}
            <span className="chat-reply-label">{button.title}</span>
            {external && <BsBoxArrowUpRight className="chat-reply-external" aria-label="Opens in a new tab" />}
          </button>
        );
      })}
      {pending && <span className="chat-reply-sr-only" role="status">Sending reply…</span>}
      {error && <p className="chat-reply-error" role="alert">Couldn’t send your reply. Please try again.</p>}
    </div>
  );
}
